import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * PWA Update Handler
 * Detects service worker updates and prompts user to refresh
 * Implements seamless update flow without requiring app reinstall
 */
export default function PWAUpdateHandler() {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  const handleUpdate = useCallback(() => {
    if (waitingWorker) {
      // Tell waiting service worker to skip waiting and activate
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
    // Reload the page to get the new version
    window.location.reload();
  }, [waitingWorker]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleServiceWorkerUpdate = (registration) => {
      if (registration.waiting) {
        setWaitingWorker(registration.waiting);
        setShowUpdatePrompt(true);
      }
    };

    // Check for updates on page load
    navigator.serviceWorker.ready.then((registration) => {
      // Check if there's already a waiting worker
      if (registration.waiting) {
        handleServiceWorkerUpdate(registration);
      }

      // Listen for new service worker installing
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available
              setWaitingWorker(newWorker);
              setShowUpdatePrompt(true);
            }
          });
        }
      });
    });

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'SW_UPDATED') {
        console.log('[PWA] Service worker updated to version:', event.data.version);
      }
    });

    // Handle controller change (service worker activated)
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });

    // Check for updates periodically (every 60 seconds)
    const checkInterval = setInterval(() => {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update();
      });
    }, 60000);

    return () => clearInterval(checkInterval);
  }, []);

  if (!showUpdatePrompt) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-4"
      data-testid="pwa-update-prompt"
    >
      <div className="bg-[#0F766E] text-white rounded-xl shadow-2xl p-4 flex items-center gap-4">
        <div className="flex-shrink-0">
          <RefreshCw className="h-6 w-6 animate-spin-slow" />
        </div>
        <div className="flex-1">
          <div className="font-semibold">New Version Available</div>
          <div className="text-sm text-white/80">Refresh to update Park Home Support</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            className="bg-white text-[#0F766E] hover:bg-white/90"
            onClick={handleUpdate}
            data-testid="pwa-refresh-btn"
          >
            Refresh
          </Button>
          <button
            onClick={() => setShowUpdatePrompt(false)}
            className="p-1 hover:bg-white/20 rounded"
            data-testid="pwa-dismiss-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
